// Сцена виджета (Ф2 «Библиотека сцены»): выбранные ассеты — персонажи, локации, предметы — и след их
// раскладки по каналам. Модульный стор (как прежний выбор персонажа и attachedStyleCard): один канал на
// оба виджета без React-контекста. Персонаж-одиночка — частный случай сцены из одного ассета.
//
// Храним выбор и след (какие url мы положили в какие каналы и чьи они) — чтобы снятие ассета убирало
// строго его фото, не трогая ручные загрузки юзера. Сам стор не переживает F5 (presigned-URL живут
// ~час, план зависит от модели); «липкость» — отдельно, в sessionStorage, только id.
import { useSyncExternalStore } from 'react'
import type { AssetKind, SceneWarning } from './assets'
import { openCharacterCreator } from '../utils/characterCreatorRequest'

export interface SceneAsset {
	id: string
	kind: AssetKind
	name: string
	/** Миниатюра для чипов и отметок (лёгкое превью, если есть). */
	avatarUrl: string | null
	isStock: boolean
}

export type SceneChannel = 'uploaded' | 'references' | 'omni'

/** Сколько ассетов держит сцена (тот же предел у сервера: scene_assets.MAX_ASSETS_PER_REQUEST). */
export const MAX_SCENE_ASSETS = 12

export interface SceneSelection {
	/** Чьи слоты заняты раскладкой: экземпляр useSceneApply (виджет раздела, страница генератора) или 'pipe' —
	 *  слот героя тренда. У каждого хоста свои слоты: сцену другого хоста новый раскладывает заново. */
	host: string
	/** Ассеты сцены в порядке добавления. */
	assets: SceneAsset[]
	/** Режим раскладки из /assets/plan ('PIPE' — слот героя тренда). */
	mode: string
	warnings: SceneWarning[]
	/** Сцена не подходит модели по существу (blocking-предупреждение): «Создать» заблокирована. */
	blocked: boolean
	/** Куда клали фото: 'omni' — отдельный канал reference_image_urls (Gemini Omni и др.). */
	channel: SceneChannel
	/** Все URL, которые МЫ положили в каналы (для чистого снятия и атрибуции). */
	placedUrls: string[]
	/** Чьи это фото: id ассета → его URL в каналах. */
	placedBy: Record<string, string[]>
	/** Ассеты без фото в запросе (не поместились в модель или модель фото не берёт) — идут словами. */
	wordsOnly: string[]
	/** Сколько фото сцены принимал канал в момент раскладки (ёмкость минус фото юзера): места стало больше —
	 *  тем, кто ждёт «словами», раскладку пересчитываем. */
	room?: number
	/** Выбор юзера в развилке «лист ↔ фото» — переживает пере-план смены модели. */
	prefer?: 'sheet' | 'photos'
	/** В слот лёг лист-карточка (для честного текста). */
	sheetUsed?: boolean
}

let current: SceneSelection | null = null
const listeners = new Set<() => void>()

function emit() {
	listeners.forEach((l) => l())
}

export function getScene(): SceneSelection | null {
	return current
}

export function setScene(sel: SceneSelection | null) {
	current = sel && sel.assets.length ? sel : null
	emit()
}

export function clearScene() {
	if (current === null) return
	current = null
	emit()
}

export function useScene(): SceneSelection | null {
	return useSyncExternalStore(
		(cb) => {
			listeners.add(cb)
			return () => listeners.delete(cb)
		},
		() => current,
	)
}

/** Первый персонаж сцены — для атрибуции character_id и фразы «the character reference photos». */
export function sceneCharacter(sel: SceneSelection | null): SceneAsset | null {
	return sel?.assets.find((a) => a.kind === 'character') ?? null
}

/** Ассет, чьё фото лежит по этому url (сверка по path: подпись presigned меняется, путь стабилен). */
export function sceneAssetOfUrl(sel: SceneSelection | null, url?: string | null): SceneAsset | null {
	if (!sel || !url) return null
	const path = (u: string) => u.split('?', 1)[0]
	const p = path(url)
	const id = Object.keys(sel.placedBy).find((k) => sel.placedBy[k].some((u) => path(u) === p))
	return id ? sel.assets.find((a) => a.id === id) ?? null : null
}

// ── «Липкая» сцена ──────────────────────────────────────────────────────
// Смена карточки/стиля чистит слоты (страж честности снимает раскладку), а F5 теряет стор — сцена
// «скидывалась». Здесь живут ТОЛЬКО id и выбор «лист ↔ фото» (presigned-URL протухают, пере-план по id
// выдаёт свежие): хост каналов авто-переприменяет сцену к новой карточке/модели. Забывается явным
// снятием юзера (× у фото, чип из промпта, «Готово» в пикере без ассета).
const STICKY_KEY = 'umnik.scene_sticky'
// Прежний ключ выбора персонажа ({id, prefer}) — переносим один раз, чтобы выбранный до обновления
// персонаж не пропал.
const LEGACY_CHARACTER_KEY = 'umnik.character_sticky'

export interface StickyScene {
	ids: string[]
	prefer?: 'sheet' | 'photos'
	/** Пути фото (без подписи ссылки), которые сцена положила в слоты: после F5 снимок слотов возвращает их обычными
	 *  фото — по путям мы убираем эти копии перед новой раскладкой, иначе фото ассета стояло бы в слотах дважды. */
	paths?: string[]
	/** Чья это сцена: вкладку мог занять другой аккаунт — чужой набор не применяем. */
	user?: string
}

// Аккаунт, которому принадлежит сцена этой вкладки (ставит AuthContext через claimSceneUser).
let sceneUser: string | null = null

export function getStickyScene(userId?: string | null): StickyScene | null {
	try {
		const raw = sessionStorage.getItem(STICKY_KEY)
		if (raw) {
			const v = JSON.parse(raw) as StickyScene
			const me = userId ?? sceneUser
			if (v?.user && me && v.user !== me) {
				sessionStorage.removeItem(STICKY_KEY)
				return null
			}
			return v?.ids?.length ? v : null
		}
		const legacy = sessionStorage.getItem(LEGACY_CHARACTER_KEY)
		if (legacy) {
			const v = JSON.parse(legacy) as { id?: string; prefer?: 'sheet' | 'photos' }
			sessionStorage.removeItem(LEGACY_CHARACTER_KEY)
			if (v?.id) {
				const moved = { ids: [v.id], prefer: v.prefer }
				sessionStorage.setItem(STICKY_KEY, JSON.stringify(moved))
				return moved
			}
		}
		return null
	} catch {
		return null
	}
}

export function setStickyScene(ids: string[], prefer?: 'sheet' | 'photos', paths?: string[]): void {
	try {
		if (!ids.length) sessionStorage.removeItem(STICKY_KEY)
		else sessionStorage.setItem(STICKY_KEY, JSON.stringify({ ids, prefer, paths: paths?.length ? paths : undefined, user: sceneUser ?? undefined }))
	} catch {
		/* quota — выбор проживёт до F5 */
	}
}

export function clearStickyScene(): void {
	try {
		sessionStorage.removeItem(STICKY_KEY)
		sessionStorage.removeItem(LEGACY_CHARACTER_KEY)
	} catch {
		/* ignore */
	}
}

// «Поколение» сцены: внешний сброс увеличивает его, и раскладка, чей запрос был в пути, уже не применяется
// (иначе ответ плана возвращал бы только что снятую сцену).
let epoch = 0

export function getSceneEpoch(): number {
	return epoch
}

/** Сбросить сцену целиком (выбор и липкость): оживление результата, «в работу», продление — там сцена лишняя. */
export function resetScene(): void {
	epoch++
	clearStickyScene()
	clearScene()
}

// Слушатели смены аккаунта (кэш меню «@», имена ассетов): api/assets подписывается сам, чтобы стор не тянул его.
const userListeners = new Set<() => void>()

export function onSceneUserChange(cb: () => void): void {
	userListeners.add(cb)
}

/** Аккаунт вкладки сменился (вход, выход, сессия кончилась): сцена, её липкость и список ассетов меню «@» —
 *  данные прежнего пользователя, следующему они не достаются. Первый вход после загрузки страницы ничего не
 *  сбрасывает — липкая сцена переживает F5 (чужую отсеет getStickyScene по полю user). */
export function claimSceneUser(userId?: string | null): void {
	const next = userId ?? null
	if (sceneUser === next) return
	const switched = sceneUser !== null
	sceneUser = next
	if (!switched) return
	resetScene()
	userListeners.forEach((cb) => cb())
}

// ── Хосты каналов ───────────────────────────────────────────────────────
// Слоты живут в GenerateProvider, а их у приложения несколько (страница генератора, виджет раздела): сцена в сторе
// одна, след раскладки — про слоты того хоста, что её клал. Каждый экземпляр useSceneApply — хост со своим id.
let hostSeq = 0
const mountedHosts: string[] = []

export function newSceneHost(): string {
	return `host-${++hostSeq}`
}

/** Хост смонтирован (возвращает функцию размонтирования). */
export function mountSceneHost(id: string): () => void {
	mountedHosts.push(id)
	return () => {
		const i = mountedHosts.indexOf(id)
		if (i >= 0) mountedHosts.splice(i, 1)
	}
}

export function isSceneHostMounted(id: string): boolean {
	return mountedHosts.includes(id)
}

/** Сцену ведёт один хост — первый из смонтированных: второй экземпляр (две панели на одном экране) не дублирует
 *  запросы плана и не спорит с первым за слоты. */
export function isPrimarySceneHost(id: string): boolean {
	return mountedHosts[0] === id
}

// Метка ПРОГРАММНОЙ очистки каналов (resetAll / применение новой карточки): страж честности отличает её от
// РУЧНОГО удаления фото юзером. Программная → раскладка снимается, липкость живёт (переприменимся к новой
// карточке); ручная → юзер осознанно убрал фото ассета — снимаем этот ассет и из липкости.
let channelsResetAt = 0

export function markSceneChannelsReset(): void {
	channelsResetAt = Date.now()
}

export function wasSceneChannelsResetRecently(): boolean {
	return Date.now() - channelsResetAt < 1500
}

// ── События ─────────────────────────────────────────────────────────────
// Живут здесь, а не в компонентах: react-refresh требует от файлов компонентов только компонент, а
// применение/снятие держит ОДИН экземпляр useSceneApply (в UploadsRow) — вторым его звать нельзя
// (внутри эффекты пере-плана сработали бы дважды на одну смену модели).

/** Открыть пикер сцены (вкладка — тип ассета). mode 'pipe' — слот героя тренда: только персонаж, один. */
export const SCENE_PICKER_EVENT = 'umnik:scene-picker-open'

export interface ScenePickerRequest {
	kind?: AssetKind
	/** scene — набор в сцену; pipe — один персонаж в слот тренда; link — выбрать связанные ассеты для страницы ассета. */
	mode?: 'scene' | 'pipe' | 'link'
	/** Начальный выбор не из стора сцены (страница «Сцена»: ассеты, упомянутые в промпте шота; связи ассета). */
	initial?: { id: string; kind: AssetKind; name: string }[]
	/** Этот ассет в списке не показывать (связи: сам с собой не связывается). */
	excludeId?: string
	/** Вкладки, которых не показывать (связи — только между разными типами: на странице персонажа нет «Персонажей»). */
	hideKinds?: AssetKind[]
	/** Заголовок окна в режиме 'link': «Связать с «Ева»». */
	title?: string
}

export function openScenePicker(req: ScenePickerRequest = {}) {
	window.dispatchEvent(new CustomEvent<ScenePickerRequest>(SCENE_PICKER_EVENT, { detail: req }))
}

/** Снять ассет из сцены (id) или всю сцену (без id). */
export const SCENE_REMOVE_EVENT = 'umnik:scene-remove'

export interface SceneRemoveRequest {
	id?: string
	/** Чип из промпта убрал сам юзер (правка текста): текст не трогаем — он уже новый, а у хука на руках ещё старый. */
	keepPrompt?: boolean
}

export function requestSceneRemove(id?: string, keepPrompt = false) {
	window.dispatchEvent(new CustomEvent<SceneRemoveRequest>(SCENE_REMOVE_EVENT, { detail: { id, keepPrompt } }))
}

// «Сделать персонажем» картинку из заполненного слота генератора или кадр «Сцены» (presigned-URL своего фото): окно
// «Новый персонаж» сразу на «Своём фото» с этой картинкой — «Готово» как есть или «Доработать» (владелец 10.10: одно
// окно вместо трёх разных входов).
export function openCharacterSave(url: string) {
	void openCharacterCreator({ start: 'own', seedUrl: url })
}
